import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, viewChild } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';

import { BaasToastMessageOptions } from '../baas-toast-message-options';
import { BaasToastMessageService } from '../baas-toast-message-service';
import { BaasViewContainerRef } from '../baas-view-container-ref';

const ICON_MAPPING = {
    success: 'fa-check',
    warning: 'fa-exclamation',
    error: 'fa-times',
    info: 'fa-info',
    neutral: 'fa-gear'
} as const;

@Component({
    selector: 'baas-toast-message',
    templateUrl: './baas-toast-message.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressBar, MatIcon, MatIconButton, BaasViewContainerRef]
})
export class BaasToastMessage implements OnDestroy {
    readonly #toastService = inject(BaasToastMessageService);
    readonly baasViewContainerRef = viewChild(BaasViewContainerRef);
    readonly elementContainer = viewChild<ElementRef<HTMLElement>>('elementContainer');
    readonly iconContainer = viewChild<ElementRef<HTMLElement>>('iconContainer');
    readonly progressBarElement = viewChild(MatProgressBar);

    readonly message = input.required<BaasToastMessageOptions>();
    protected readonly progress = signal<number>(98);
    private interval;

    constructor() {
        effect(() => {
            const progressBarElement = this.progressBarElement();
            const progressBarEl = progressBarElement?._elementRef.nativeElement;
            if (!progressBarEl) return;
            this.#setIntervalForMessage(progressBarEl);
        });

        effect(() => {
            const container = this.elementContainer();
            const element = this.message()._element;
            if (container && element) {
                container.nativeElement.appendChild(element);
            }
        });

        effect(() => {
            const container = this.iconContainer();
            const icon = this.message().icon;
            if (container && icon instanceof HTMLElement) {
                container.nativeElement.appendChild(icon);
            }
        });

        effect(() => {
            const viewContainerRef = this.baasViewContainerRef()?.viewContainerRef;
            const component = this.message().component;
            if (!viewContainerRef || !component) return;

            viewContainerRef.clear();
            const componentRef = viewContainerRef.createComponent(component);
            componentRef.setInput('data', this.message().componentParameters);
        });
    }

    protected readonly typeClass = computed(() => {
        return `bar-toast-message__item--${this.message().type}`;
    });

    protected readonly typeIcon = computed(() => {
        const icon = this.message().icon;

        // When an HTMLElement is provided, the element is injected into the DOM directly — no CSS class needed
        if (icon instanceof HTMLElement) return '';

        const iconStr = icon || ICON_MAPPING[this.message().type];

        // If icon starts with 'fa-' but doesn't have a style prefix (fas, far, fab, fal, fad), add 'fas' by default
        if (iconStr.startsWith('fa-') && !/(^|\s)(fas|far|fab|fal|fad)(\s|$)/.test(iconStr)) {
            return `fas ${iconStr}`;
        }
        return iconStr;
    });

    ngOnDestroy(): void {
        if (this.interval) clearInterval(this.interval);
    }

    protected removeMessage(): void {
        this.#toastService.removeMessage(this.message().id, 'user');
    }

    #setIntervalForMessage(progressBarNativeElement: HTMLElement): void {
        const stepInterval = Math.floor(this.message().duration / 100);
        const progressFillElement: HTMLElement = progressBarNativeElement.querySelector('.mdc-linear-progress__bar.mdc-linear-progress__primary-bar');
        if (!progressFillElement) return;
        progressFillElement.style.transitionDuration = `${stepInterval}ms`;
        progressFillElement.style.transitionTimingFunction = 'linear';

        this.interval = setInterval(() => {
            this.progress.update((progress) => progress - 1);
            if (this.progress() <= 0) clearInterval(this.interval);
        }, stepInterval);
    }
}
